import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Mail, Eye, RefreshCw } from "lucide-react";

const STATUS_COLOR = {
  sent: "#16a34a",
  logged: "#3b82f6",
  error: "#dc2626",
  queued: "#6b7280",
};

const TEMPLATE_LABEL = {
  boas_vindas: "Boas-vindas (cliente)",
  status_ticket: "Status do ticket",
  novo_usuario: "Novo usuário",
  vencimento_contrato: "Vencimento contrato",
  inadimplencia: "Inadimplência",
};

export default function EmailLog() {
  const [logs, setLogs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email-log");
      setLogs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
            Log de e-mails
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Auditoria de todos os e-mails disparados (modo log enquanto SENDGRID_API_KEY não está configurada)
          </p>
        </div>
        <Button onClick={load} variant="outline" disabled={loading} data-testid="email-log-refresh">
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Para</TableHead>
              <TableHead>Assunto</TableHead>
              <TableHead>Modo</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ver</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((l) => (
              <TableRow key={l.id} data-testid={`email-row-${l.id}`}>
                <TableCell className="text-xs whitespace-nowrap">
                  {new Date(l.sent_at).toLocaleString("pt-BR")}
                </TableCell>
                <TableCell className="text-xs">
                  <Badge variant="outline">{TEMPLATE_LABEL[l.template] || l.template}</Badge>
                </TableCell>
                <TableCell className="text-xs">{l.to}</TableCell>
                <TableCell className="text-xs max-w-[280px] truncate">{l.subject}</TableCell>
                <TableCell>
                  <Badge
                    style={{
                      background: l.mode === "sendgrid" ? "#16a34a" : "#6b7280",
                      color: "white",
                    }}
                  >
                    {l.mode === "sendgrid" ? "SendGrid" : "Log"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge style={{ background: STATUS_COLOR[l.status] || "#6b7280", color: "white" }}>
                    {l.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => setDetail(l)} data-testid={`email-view-${l.id}`}>
                    <Eye size={12} className="mr-1" /> Pré-visualizar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  <Mail size={20} className="inline mr-2" /> Nenhum e-mail registrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detail?.subject}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><strong>Para:</strong> {detail.to}</div>
                <div><strong>De:</strong> {detail.from_email}</div>
                <div><strong>CC:</strong> {detail.cc?.join(", ") || "—"}</div>
                <div><strong>Template:</strong> {detail.template}</div>
                <div><strong>Modo:</strong> {detail.mode}</div>
                <div><strong>Status:</strong> {detail.status}</div>
              </div>
              <div className="border rounded-md p-3 bg-slate-50 max-h-[400px] overflow-y-auto">
                <iframe
                  title="email preview"
                  srcDoc={detail.html_preview}
                  className="w-full"
                  style={{ minHeight: 400, border: "0" }}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
